import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { CheckCircle2, XCircle, AlertCircle, MinusCircle, Clock, AlertTriangle, CheckCircle, AlertOctagon, Activity, FileText, Users, RefreshCw, Shield, UserCheck, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { getReviewInfo } from '../utils/policyReview';
import { ActionsDashboardSection, RiskDashboardSection, IncidentDashboardSection, PolicyDashboardSection, SupplierDashboardSection } from '../components/DashboardRegisterSections';
import { Button } from '../components/ui/button';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { viewingOrg, enabledFrameworks } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [iso27001Stats, setIso27001Stats] = useState(null);
  const [soc2Stats, setSoc2Stats] = useState(null);
  const [essential8Stats, setEssential8Stats] = useState(null);
  const [pcidssStats, setPcidssStats] = useState(null);
  const [hipaaStats, setHipaaStats] = useState(null);
  const [iso42001Stats, setIso42001Stats] = useState(null);
  const [sociStats, setSociStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFramework, setSelectedFramework] = useState('all');
  const [m365SecureScore, setM365SecureScore] = useState(null);

  // Helper to check if a framework is enabled
  const isFrameworkEnabled = (frameworkId) => {
    if (!enabledFrameworks || enabledFrameworks.length === 0) return true; // Show all if not loaded
    return enabledFrameworks.includes(frameworkId);
  };

  useEffect(() => {
    fetchStats();
    fetchM365SecureScore();
    
    // Set up auto-refresh every 5 minutes
    const refreshInterval = setInterval(() => {
      fetchStats();
      fetchM365SecureScore();
    }, 300000);
    
    // Cleanup interval on component unmount
    return () => clearInterval(refreshInterval);
  }, [selectedFramework, viewingOrg, enabledFrameworks]);

  const [riskStats, setRiskStats] = useState(null);
  const [incidentStats, setIncidentStats] = useState(null);
  const [actionStats, setActionStats] = useState(null);
  const [policyStats, setPolicyStats] = useState(null);
  const [supplierStats, setSupplierStats] = useState(null);

  // Build query param for super admin viewing org
  const getViewTenantParam = () => {
    return viewingOrg?.id ? `&view_tenant_id=${viewingOrg.id}` : '';
  };

  const fetchM365SecureScore = async () => {
    try {
      const viewParam = viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      const response = await axios.get(`${API}/m365/security-posture${viewParam}`);
      if (response.data && !response.data.error && !response.data.not_connected && response.data.secure_score !== undefined) {
        setM365SecureScore({
          score: response.data.secure_score,
          maxScore: response.data.max_score,
          percentage: response.data.secure_score_percentage,
          completedControls: response.data.completed_count,
          totalControls: response.data.total_controls,
          toAddressCount: response.data.to_address_count
        });
      } else {
        setM365SecureScore(null);
      }
    } catch (error) {
      setM365SecureScore(null);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    const viewParam = viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
    try {
      if (selectedFramework === 'all') {
        // Build array of fetch promises only for enabled frameworks
        const fetchPromises = [];
        const frameworkNames = [];
        
        const frameworkEndpoints = {
          'ISO27001': 'iso27001',
          'SOC2': 'soc2',
          'Essential8': 'Essential8',
          'PCIDSS': 'PCIDSS',
          'HIPAA': 'HIPAA',
          'ISO42001': 'ISO42001',
          'SOCI': 'SOCI',
        };
        
        for (const [fwId, fwEndpoint] of Object.entries(frameworkEndpoints)) {
          if (isFrameworkEnabled(fwId)) {
            fetchPromises.push(axios.get(`${API}/frameworks/${fwEndpoint}/stats${viewParam}`));
            frameworkNames.push(fwId);
          }
        }
        
        // Add other stats — Risk Register sources from the new Risk Engine so
        // the home dashboard stays in sync with /risk-engine (single source
        // of truth). Legacy /risks endpoint is no longer consulted here.
        fetchPromises.push(
          axios.get(`${API}/risk-engine/risks${viewParam}`),
          axios.get(`${API}/incidents`),
          axios.get(`${API}/policies`),
          axios.get(`${API}/suppliers`),
          axios.get(`${API}/policy-ack/tracking/report${viewParam}`).catch(() => ({ data: null })),
          axios.get(`${API}/improvement-actions${viewParam}`).catch(() => ({ data: [] }))
        );
        
        const responses = await Promise.all(fetchPromises);
        
        // Reset all framework stats first
        setIso27001Stats(null);
        setSoc2Stats(null);
        setEssential8Stats(null);
        setPcidssStats(null);
        setHipaaStats(null);
        setIso42001Stats(null);
        setSociStats(null);
        
        // Map responses back to framework stats (add pending alias for not_assessed)
        let responseIndex = 0;
        frameworkNames.forEach(fw => {
          const data = { ...responses[responseIndex].data, pending: responses[responseIndex].data.not_assessed || 0 };
          switch(fw) {
            case 'ISO27001': setIso27001Stats(data); break;
            case 'SOC2': setSoc2Stats(data); break;
            case 'Essential8': setEssential8Stats(data); break;
            case 'PCIDSS': setPcidssStats(data); break;
            case 'HIPAA': setHipaaStats(data); break;
            case 'ISO42001': setIso42001Stats(data); break;
            case 'SOCI': setSociStats(data); break;
          }
          responseIndex++;
        });
        
        setStats(null);
        
        // Get other stats from remaining responses
        const risksResponse = responses[responseIndex++];
        const incidentsResponse = responses[responseIndex++];
        const policiesResponse = responses[responseIndex++];
        const suppliersResponse = responses[responseIndex++];
        const ackReportResponse = responses[responseIndex++];
        const actionsResponse = responses[responseIndex++];
        
        // Calculate risk statistics from Risk Engine payload.
        // Response shape: { risks: [...], summary: {total, by_rating, by_status} }
        // Risks now surface only for Non Compliant + Partially Compliant controls.
        const riskPayload = risksResponse.data || {};
        const risks = Array.isArray(riskPayload) ? riskPayload : (riskPayload.risks || []);
        const summary = riskPayload.summary || {};
        const countBy = (arr, key, fallback) => arr.reduce((acc, x) => {
          const k = x[key] || fallback;
          acc[k] = (acc[k] || 0) + 1;
          return acc;
        }, {});
        setRiskStats({
          total: summary.total ?? risks.length,
          byRating: summary.by_rating || countBy(risks, 'risk_rating'),
          byStatus: summary.by_status || countBy(risks, 'status', 'Open'),
        });
        
        // Calculate incident statistics
        const incidents = incidentsResponse.data;
        setIncidentStats({
          total: incidents.length,
          bySeverity: countBy(incidents, 'severity'),
          byStatus: countBy(incidents, 'status'),
        });

        const actions = actionsResponse.data || [];
        const today = new Date(new Date().toDateString());
        const actionStatus = countBy(actions, 'status');
        setActionStats({
          total: actions.length,
          open: actionStatus.Open || 0,
          inProgress: actionStatus['In Progress'] || 0,
          pendingReview: actionStatus['Pending Review'] || 0,
          completed: actionStatus.Completed || 0,
          closed: actionStatus.Closed || 0,
          overdue: actions.filter(a => a.date_due && !['Completed', 'Closed'].includes(a.status) && new Date(a.date_due) < today).length,
        });
        
        // Calculate policy statistics
        const policies = policiesResponse.data;
        const ackData = ackReportResponse?.data || null;
        setPolicyStats({
          total: policies.length,
          approved: policies.filter(p => p.status === 'Approved').length,
          underReview: policies.filter(p => p.status === 'Under Review').length,
          draft: policies.filter(p => p.status === 'Draft').length,
          archived: policies.filter(p => p.status === 'Archived').length,
          ...policies.reduce((acc, p) => {
            const info = getReviewInfo(p);
            if (!info) return acc;
            if (info.key === 'overdue') acc.reviewOverdue += 1;
            else if (info.key === 'due_soon') acc.reviewDueSoon += 1;
            else acc.reviewCurrent += 1;
            return acc;
          }, { reviewOverdue: 0, reviewDueSoon: 0, reviewCurrent: 0 }),
          ackTotal: ackData?.total_records || 0,
          ackAcknowledged: ackData?.acknowledged_records || 0,
          ackPending: ackData?.pending_records || 0,
          ackPercent: ackData?.completion_pct || 0,
        });
        
        // Calculate supplier statistics
        const suppliers = suppliersResponse.data;
        setSupplierStats({
          total: suppliers.length,
          active: suppliers.filter(s => s.status === 'Active').length,
          critical: suppliers.filter(s => s.criticality === 'Critical').length,
          highRisk: suppliers.filter(s => s.risk_level === 'High' || s.risk_level === 'Critical').length,
          compliant: suppliers.filter(s => s.compliance_status === 'Compliant').length
        });
      } else {
        const frameworkEndpoints = {
          'ISO27001': 'iso27001', 'SOC2': 'soc2', 'Essential8': 'Essential8',
          'PCIDSS': 'PCIDSS', 'HIPAA': 'HIPAA', 'ISO42001': 'ISO42001', 'SOCI': 'SOCI',
          'CyberForte': 'cyberforte'
        };
        const endpoint = frameworkEndpoints[selectedFramework] || selectedFramework;
        const response = await axios.get(`${API}/frameworks/${endpoint}/stats${viewParam}`);
        const data = { ...response.data, pending: response.data.not_assessed || 0 };
        setStats(data);
        setIso27001Stats(null);
        setSoc2Stats(null);
      }
    } catch (error) {
      toast.error('Failed to fetch dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const renderFrameworkStats = (frameworkStats, frameworkName, frameworkColor) => {
    const pieData = [
      { name: 'Compliant', value: frameworkStats.compliant, color: '#10B981' },
      { name: 'Non-Compliant', value: frameworkStats.non_compliant, color: '#EF4444' },
      { name: 'Partially Compliant', value: frameworkStats.partial, color: '#F59E0B' },
                          { name: 'Not Assessed', value: frameworkStats.not_assessed || 0, color: '#94A3B8' },
      { name: 'N.A', value: frameworkStats.not_applicable, color: '#64748B' },
    ].filter(item => item.value > 0);
    
    const total = frameworkStats.total || (frameworkStats.compliant + frameworkStats.non_compliant + frameworkStats.partial + (frameworkStats.not_applicable || 0));

    return (
      <div className="space-y-6">
        {/* Framework Header */}
        <div className="flex items-center gap-3">
          <div className={`px-4 py-2 rounded-sm text-sm font-semibold ${frameworkColor}`}>
            {frameworkName}
          </div>
          <div className="text-sm text-slate-600">
            {frameworkStats.compliance_percentage}% Complete
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="stat-card bg-white" data-testid={`${frameworkName}-total-card`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Controls</p>
                <p className="text-3xl font-heading font-semibold mt-2 text-slate-900">{frameworkStats.total}</p>
              </div>
              <div className="p-2 bg-slate-100 rounded-sm">
                <ShieldCheck className="w-5 h-5 text-slate-600" />
              </div>
            </div>
          </div>

          <div className="stat-card bg-white">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Compliant</p>
                <p className="text-3xl font-heading font-semibold mt-2 text-status-compliant">
                  {frameworkStats.compliant}
                </p>
              </div>
              <CheckCircle2 className="w-6 h-6 text-status-compliant" />
            </div>
          </div>

          <div className="stat-card bg-white">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Non-Compliant</p>
                <p className="text-3xl font-heading font-semibold mt-2 text-status-noncompliant">
                  {frameworkStats.non_compliant}
                </p>
              </div>
              <XCircle className="w-6 h-6 text-status-noncompliant" />
            </div>
          </div>

          <div className="stat-card bg-white">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Partially Compliant</p>
                <p className="text-3xl font-heading font-semibold mt-2 text-status-partial">
                  {frameworkStats.partial}
                </p>
              </div>
              <AlertCircle className="w-6 h-6 text-status-partial" />
            </div>
          </div>
        </div>

        {/* Compliance Progress */}
        <div className="stat-card bg-white">
          <h2 className="text-xl font-heading font-semibold mb-4 text-slate-900">Compliance Progress</h2>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="h-8 bg-slate-100 rounded-sm overflow-hidden">
                <div
                  className="h-full bg-status-compliant transition-all duration-500"
                  style={{ width: `${frameworkStats.compliance_percentage}%` }}
                />
              </div>
            </div>
            <div className="text-2xl font-heading font-semibold">
              {frameworkStats.compliance_percentage}%
            </div>
          </div>
        </div>

        {/* Compliance Status Distribution */}
        <div className="stat-card bg-white">
          <h2 className="text-xl font-heading font-semibold mb-4 text-slate-900">Compliance Status Distribution</h2>
          <div className="flex flex-col items-center">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => {
                    const pct = total > 0 ? (value / total * 100).toFixed(0) : 0;
                    return pct > 5 ? `${name}: ${pct}%` : '';
                  }}
                  outerRadius={100}
                  fill="#64748B"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => {
                  const pct = total > 0 ? (value / total * 100).toFixed(1) : 0;
                  return [`${value} (${pct}%)`, ''];
                }} />
              </PieChart>
            </ResponsiveContainer>
            
            {/* Legend */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 w-full">
              {pieData.map((entry, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: entry.color }}></div>
                  <span className="text-sm text-slate-600">{entry.name}: {entry.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-slate-600">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight text-slate-900">
            Compliance Dashboard
          </h1>
          <p className="text-gray-700 mt-2">Overview of compliance assessments</p>
        </div>
        
        <div className="flex gap-2">
          {/* Manual Refresh Button */}
          <Button
            onClick={() => {
              fetchStats();
              toast.success('Dashboard refreshed');
            }}
            variant="outline"
            size="sm"
            className="rounded-sm"
            title="Refresh dashboard data"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          
          {/* Framework Filter */}
          <select
            data-testid="framework-filter"
            value={selectedFramework}
            onChange={(e) => setSelectedFramework(e.target.value)}
            className="rounded-sm border border-slate-700 bg-white text-slate-900 px-4 py-2 text-sm font-medium"
          >
            <option value="all">All Frameworks</option>
            {isFrameworkEnabled('ISO27001') && <option value="ISO27001">ISO 27001</option>}
            {isFrameworkEnabled('SOC2') && <option value="SOC2">SOC 2</option>}
            {isFrameworkEnabled('Essential8') && <option value="Essential8">Essential 8</option>}
            {isFrameworkEnabled('PCIDSS') && <option value="PCIDSS">PCI DSS</option>}
            {isFrameworkEnabled('HIPAA') && <option value="HIPAA">HIPAA</option>}
            {isFrameworkEnabled('ISO42001') && <option value="ISO42001">ISO 42001</option>}
            {isFrameworkEnabled('SOCI') && <option value="SOCI">SOCI</option>}
          </select>
        </div>
      </div>

      {/* Render based on selection */}
      {selectedFramework === 'all' ? (
        <div className="space-y-12">
          {/* Microsoft 365 Secure Score - Only shown if connected */}
          {m365SecureScore && (
            <div 
              className="bg-gradient-to-r from-[#0078D4] to-[#106EBE] rounded-sm p-6 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate('/m365-integration')}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Microsoft Secure Score</p>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-4xl font-bold text-white">{m365SecureScore.score || 0}</span>
                      <span className="text-xl text-blue-200">/ {m365SecureScore.maxScore || 0}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white">{m365SecureScore.percentage || 0}%</p>
                    <p className="text-xs text-blue-200">Score</p>
                  </div>
                  <div className="h-12 w-px bg-white/30"></div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-300">{m365SecureScore.completedControls || 0}</p>
                    <p className="text-xs text-blue-200">Completed</p>
                  </div>
                  <div className="h-12 w-px bg-white/30"></div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-red-300">{m365SecureScore.toAddressCount || 0}</p>
                    <p className="text-xs text-blue-200">To Address</p>
                  </div>
                  <div className="h-12 w-px bg-white/30"></div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white">{m365SecureScore.totalControls || 21}</p>
                    <p className="text-xs text-blue-200">Total Controls</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Side-by-Side Compliance Status Distribution */}
          {(iso27001Stats || soc2Stats || essential8Stats || pcidssStats || hipaaStats || iso42001Stats || sociStats) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {/* ISO 27001 Compliance Status Distribution */}
              {isFrameworkEnabled('ISO27001') && iso27001Stats && (
              <div className="stat-card bg-white border-t-4 border-slate-600 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/iso27001')}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="px-4 py-2 rounded-sm text-sm font-semibold bg-slate-100 text-slate-800">
                    ISO 27001
                  </div>
                  <div className="text-sm text-slate-600">
                    {iso27001Stats.compliance_percentage}% Complete
                  </div>
                </div>
                <h2 className="text-xl font-heading font-semibold mb-4 text-slate-900">Compliance Status Distribution</h2>
                <div className="flex flex-col items-center">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Compliant', value: iso27001Stats.compliant, color: '#10B981' },
                          { name: 'Non-Compliant', value: iso27001Stats.non_compliant, color: '#EF4444' },
                          { name: 'Partially Compliant', value: iso27001Stats.partial, color: '#F59E0B' },
                          { name: 'Not Assessed', value: iso27001Stats.not_assessed || 0, color: '#94A3B8' },
                        ].filter(item => item.value > 0)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ value }) => {
                          const total = iso27001Stats.total || (iso27001Stats.compliant + iso27001Stats.non_compliant + iso27001Stats.partial + (iso27001Stats.not_assessed || 0));
                          const pct = total > 0 ? (value / total * 100).toFixed(0) : 0;
                          return pct > 5 ? `${pct}%` : '';
                        }}
                        outerRadius={90}
                        fill="#64748B"
                        dataKey="value"
                      >
                        {[
                          { name: 'Compliant', value: iso27001Stats.compliant, color: '#10B981' },
                          { name: 'Non-Compliant', value: iso27001Stats.non_compliant, color: '#EF4444' },
                          { name: 'Partially Compliant', value: iso27001Stats.partial, color: '#F59E0B' },
                          { name: 'Not Assessed', value: iso27001Stats.not_assessed || 0, color: '#94A3B8' },
                        ].filter(item => item.value > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => {
                        const total = iso27001Stats.total || (iso27001Stats.compliant + iso27001Stats.non_compliant + iso27001Stats.partial + (iso27001Stats.not_assessed || 0));
                        const pct = total > 0 ? (value / total * 100).toFixed(1) : 0;
                        return [`${value} (${pct}%)`, ''];
                      }} />
                    </PieChart>
                  </ResponsiveContainer>
                  
                  {/* Legend */}
                  <div className="grid grid-cols-2 gap-3 mt-4 w-full">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-green-500"></div>
                      <span className="text-sm text-slate-600">Compliant: {iso27001Stats.compliant}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-red-500"></div>
                      <span className="text-sm text-slate-600">Non-Compliant: {iso27001Stats.non_compliant}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-orange-500"></div>
                      <span className="text-sm text-slate-600">Partially Compliant: {iso27001Stats.partial}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-slate-500"></div>
                      <span className="text-sm text-slate-600">Not Applicable: {iso27001Stats.not_applicable || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-slate-400"></div>
                      <span className="text-sm text-slate-600">Not Assessed: {iso27001Stats.not_assessed || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* SOC 2 Compliance Status Distribution */}
              {isFrameworkEnabled('SOC2') && soc2Stats && (
              <div className="stat-card bg-white border-t-4 border-slate-600 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/soc2')}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="px-4 py-2 rounded-sm text-sm font-semibold bg-slate-100 text-slate-800">
                    SOC 2
                  </div>
                  <div className="text-sm text-slate-600">
                    {soc2Stats.compliance_percentage}% Complete
                  </div>
                </div>
                <h2 className="text-xl font-heading font-semibold mb-4 text-slate-900">Compliance Status Distribution</h2>
                <div className="flex flex-col items-center">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Compliant', value: soc2Stats.compliant, color: '#10B981' },
                          { name: 'Non-Compliant', value: soc2Stats.non_compliant, color: '#EF4444' },
                          { name: 'Partially Compliant', value: soc2Stats.partial, color: '#F59E0B' },
                          { name: 'Not Assessed', value: soc2Stats.not_assessed || 0, color: '#94A3B8' },
                        ].filter(item => item.value > 0)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ value }) => {
                          const total = soc2Stats.total || (soc2Stats.compliant + soc2Stats.non_compliant + soc2Stats.partial + (soc2Stats.not_assessed || 0));
                          const pct = total > 0 ? (value / total * 100).toFixed(0) : 0;
                          return pct > 5 ? `${pct}%` : '';
                        }}
                        outerRadius={90}
                        fill="#64748B"
                        dataKey="value"
                      >
                        {[
                          { name: 'Compliant', value: soc2Stats.compliant, color: '#10B981' },
                          { name: 'Non-Compliant', value: soc2Stats.non_compliant, color: '#EF4444' },
                          { name: 'Partially Compliant', value: soc2Stats.partial, color: '#F59E0B' },
                          { name: 'Not Assessed', value: soc2Stats.not_assessed || 0, color: '#94A3B8' },
                        ].filter(item => item.value > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => {
                        const total = soc2Stats.total || (soc2Stats.compliant + soc2Stats.non_compliant + soc2Stats.partial + (soc2Stats.not_assessed || 0));
                        const pct = total > 0 ? (value / total * 100).toFixed(1) : 0;
                        return [`${value} (${pct}%)`, ''];
                      }} />
                    </PieChart>
                  </ResponsiveContainer>
                  
                  {/* Legend */}
                  <div className="grid grid-cols-2 gap-3 mt-4 w-full">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-green-500"></div>
                      <span className="text-sm text-slate-600">Compliant: {soc2Stats.compliant}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-red-500"></div>
                      <span className="text-sm text-slate-600">Non-Compliant: {soc2Stats.non_compliant}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-orange-500"></div>
                      <span className="text-sm text-slate-600">Partially Compliant: {soc2Stats.partial}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-slate-500"></div>
                      <span className="text-sm text-slate-600">Not Applicable: {soc2Stats.not_applicable || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-slate-400"></div>
                      <span className="text-sm text-slate-600">Not Assessed: {soc2Stats.not_assessed || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* Essential 8 Compliance Status Distribution */}
              {isFrameworkEnabled('Essential8') && essential8Stats && (
              <div className="stat-card bg-white border-t-4 border-slate-600 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/essential8')}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="px-4 py-2 rounded-sm text-sm font-semibold bg-slate-100 text-slate-800">
                    Essential 8
                  </div>
                  <div className="text-sm text-slate-600">
                    {essential8Stats.compliance_percentage}% Complete
                  </div>
                </div>
                <h2 className="text-xl font-heading font-semibold mb-4 text-slate-900">Compliance Status Distribution</h2>
                <div className="flex flex-col items-center">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Compliant', value: essential8Stats.compliant, color: '#10B981' },
                          { name: 'Non-Compliant', value: essential8Stats.non_compliant, color: '#EF4444' },
                          { name: 'Partially Compliant', value: essential8Stats.partial, color: '#F59E0B' },
                          { name: 'Not Assessed', value: essential8Stats.not_assessed || 0, color: '#94A3B8' },
                        ].filter(item => item.value > 0)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ value }) => {
                          const total = essential8Stats.total || (essential8Stats.compliant + essential8Stats.non_compliant + essential8Stats.partial + (essential8Stats.not_assessed || 0));
                          const pct = total > 0 ? (value / total * 100).toFixed(0) : 0;
                          return pct > 5 ? `${pct}%` : '';
                        }}
                        outerRadius={90}
                        fill="#64748B"
                        dataKey="value"
                      >
                        {[
                          { name: 'Compliant', value: essential8Stats.compliant, color: '#10B981' },
                          { name: 'Non-Compliant', value: essential8Stats.non_compliant, color: '#EF4444' },
                          { name: 'Partially Compliant', value: essential8Stats.partial, color: '#F59E0B' },
                          { name: 'Not Assessed', value: essential8Stats.not_assessed || 0, color: '#94A3B8' },
                        ].filter(item => item.value > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => {
                        const total = essential8Stats.total || (essential8Stats.compliant + essential8Stats.non_compliant + essential8Stats.partial + (essential8Stats.not_assessed || 0));
                        const pct = total > 0 ? (value / total * 100).toFixed(1) : 0;
                        return [`${value} (${pct}%)`, ''];
                      }} />
                    </PieChart>
                  </ResponsiveContainer>
                  
                  {/* Legend */}
                  <div className="grid grid-cols-2 gap-3 mt-4 w-full">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-green-500"></div>
                      <span className="text-sm text-slate-600">Compliant: {essential8Stats.compliant}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-red-500"></div>
                      <span className="text-sm text-slate-600">Non-Compliant: {essential8Stats.non_compliant}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-orange-500"></div>
                      <span className="text-sm text-slate-600">Partially Compliant: {essential8Stats.partial}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-slate-500"></div>
                      <span className="text-sm text-slate-600">Not Applicable: {essential8Stats.not_applicable || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-slate-400"></div>
                      <span className="text-sm text-slate-600">Not Assessed: {essential8Stats.not_assessed || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* PCI DSS Compliance Status Distribution */}
              {isFrameworkEnabled('PCIDSS') && pcidssStats && (
              <div className="stat-card bg-white border-t-4 border-slate-600 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/pci-dss')}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="px-4 py-2 rounded-sm text-sm font-semibold bg-slate-100 text-slate-800">
                    PCI DSS
                  </div>
                  <div className="text-sm text-slate-600">
                    {pcidssStats.compliance_percentage}% Complete
                  </div>
                </div>
                <h2 className="text-xl font-heading font-semibold mb-4 text-slate-900">Compliance Status Distribution</h2>
                <div className="flex flex-col items-center">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Compliant', value: pcidssStats.compliant, color: '#10B981' },
                          { name: 'Non-Compliant', value: pcidssStats.non_compliant, color: '#EF4444' },
                          { name: 'Partially Compliant', value: pcidssStats.partial, color: '#F59E0B' },
                          { name: 'Not Assessed', value: pcidssStats.not_assessed || 0, color: '#94A3B8' },
                        ].filter(item => item.value > 0)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ value }) => {
                          const total = pcidssStats.total || (pcidssStats.compliant + pcidssStats.non_compliant + pcidssStats.partial + (pcidssStats.not_assessed || 0));
                          const pct = total > 0 ? (value / total * 100).toFixed(0) : 0;
                          return pct > 5 ? `${pct}%` : '';
                        }}
                        outerRadius={90}
                        fill="#64748B"
                        dataKey="value"
                      >
                        {[
                          { name: 'Compliant', value: pcidssStats.compliant, color: '#10B981' },
                          { name: 'Non-Compliant', value: pcidssStats.non_compliant, color: '#EF4444' },
                          { name: 'Partially Compliant', value: pcidssStats.partial, color: '#F59E0B' },
                          { name: 'Not Assessed', value: pcidssStats.not_assessed || 0, color: '#94A3B8' },
                        ].filter(item => item.value > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => {
                        const total = pcidssStats.total || (pcidssStats.compliant + pcidssStats.non_compliant + pcidssStats.partial + (pcidssStats.not_assessed || 0));
                        const pct = total > 0 ? (value / total * 100).toFixed(1) : 0;
                        return [`${value} (${pct}%)`, ''];
                      }} />
                    </PieChart>
                  </ResponsiveContainer>
                  
                  {/* Legend */}
                  <div className="grid grid-cols-2 gap-3 mt-4 w-full">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-green-500"></div>
                      <span className="text-sm text-slate-600">Compliant: {pcidssStats.compliant}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-red-500"></div>
                      <span className="text-sm text-slate-600">Non-Compliant: {pcidssStats.non_compliant}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-orange-500"></div>
                      <span className="text-sm text-slate-600">Partially Compliant: {pcidssStats.partial}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-slate-500"></div>
                      <span className="text-sm text-slate-600">Not Applicable: {pcidssStats.not_applicable || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-slate-400"></div>
                      <span className="text-sm text-slate-600">Not Assessed: {pcidssStats.not_assessed || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* HIPAA Compliance Status Distribution */}
              {isFrameworkEnabled('HIPAA') && hipaaStats && (
              <div className="stat-card bg-white border-t-4 border-slate-600 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/hipaa')}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="px-4 py-2 rounded-sm text-sm font-semibold bg-slate-100 text-slate-800">
                    HIPAA
                  </div>
                  <div className="text-sm text-slate-600">
                    {hipaaStats.compliance_percentage}% Complete
                  </div>
                </div>
                <h2 className="text-xl font-heading font-semibold mb-4 text-slate-900">Compliance Status Distribution</h2>
                <div className="flex flex-col items-center">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Compliant', value: hipaaStats.compliant, color: '#10B981' },
                          { name: 'Non-Compliant', value: hipaaStats.non_compliant, color: '#EF4444' },
                          { name: 'Partially Compliant', value: hipaaStats.partial, color: '#F59E0B' },
                          { name: 'Not Assessed', value: hipaaStats.not_assessed || 0, color: '#94A3B8' },
                        ].filter(item => item.value > 0)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ value }) => {
                          const total = hipaaStats.total || (hipaaStats.compliant + hipaaStats.non_compliant + hipaaStats.partial + (hipaaStats.not_assessed || 0));
                          const pct = total > 0 ? (value / total * 100).toFixed(0) : 0;
                          return pct > 5 ? `${pct}%` : '';
                        }}
                        outerRadius={90}
                        fill="#64748B"
                        dataKey="value"
                      >
                        {[
                          { name: 'Compliant', value: hipaaStats.compliant, color: '#10B981' },
                          { name: 'Non-Compliant', value: hipaaStats.non_compliant, color: '#EF4444' },
                          { name: 'Partially Compliant', value: hipaaStats.partial, color: '#F59E0B' },
                          { name: 'Not Assessed', value: hipaaStats.not_assessed || 0, color: '#94A3B8' },
                        ].filter(item => item.value > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => {
                        const total = hipaaStats.total || (hipaaStats.compliant + hipaaStats.non_compliant + hipaaStats.partial + (hipaaStats.not_assessed || 0));
                        const pct = total > 0 ? (value / total * 100).toFixed(1) : 0;
                        return [`${value} (${pct}%)`, ''];
                      }} />
                    </PieChart>
                  </ResponsiveContainer>
                  
                  {/* Legend */}
                  <div className="grid grid-cols-2 gap-3 mt-4 w-full">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-green-500"></div>
                      <span className="text-sm text-slate-600">Compliant: {hipaaStats.compliant}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-red-500"></div>
                      <span className="text-sm text-slate-600">Non-Compliant: {hipaaStats.non_compliant}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-orange-500"></div>
                      <span className="text-sm text-slate-600">Partially Compliant: {hipaaStats.partial}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-slate-500"></div>
                      <span className="text-sm text-slate-600">Not Applicable: {hipaaStats.not_applicable || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-slate-400"></div>
                      <span className="text-sm text-slate-600">Not Assessed: {hipaaStats.not_assessed || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* ISO 42001 Compliance Status Distribution */}
              {isFrameworkEnabled('ISO42001') && iso42001Stats && (
              <div className="stat-card bg-white border-t-4 border-violet-600 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/iso42001')}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="px-4 py-2 rounded-sm text-sm font-semibold bg-violet-100 text-violet-800">
                    ISO 42001
                  </div>
                  <div className="text-sm text-slate-600">
                    {iso42001Stats.compliance_percentage}% Complete
                  </div>
                </div>
                <h2 className="text-xl font-heading font-semibold mb-4 text-slate-900">Compliance Status Distribution</h2>
                <div className="flex flex-col items-center">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Compliant', value: iso42001Stats.compliant, color: '#10B981' },
                          { name: 'Non-Compliant', value: iso42001Stats.non_compliant, color: '#EF4444' },
                          { name: 'Partially Compliant', value: iso42001Stats.partial, color: '#F59E0B' },
                          { name: 'Not Assessed', value: iso42001Stats.not_assessed || 0, color: '#94A3B8' },
                        ].filter(item => item.value > 0)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ value }) => {
                          const total = iso42001Stats.total || (iso42001Stats.compliant + iso42001Stats.non_compliant + iso42001Stats.partial + (iso42001Stats.not_assessed || 0));
                          const pct = total > 0 ? (value / total * 100).toFixed(0) : 0;
                          return pct > 5 ? `${pct}%` : '';
                        }}
                        outerRadius={90}
                        fill="#8B5CF6"
                        dataKey="value"
                      >
                        {[
                          { name: 'Compliant', value: iso42001Stats.compliant, color: '#10B981' },
                          { name: 'Non-Compliant', value: iso42001Stats.non_compliant, color: '#EF4444' },
                          { name: 'Partially Compliant', value: iso42001Stats.partial, color: '#F59E0B' },
                          { name: 'Not Assessed', value: iso42001Stats.not_assessed || 0, color: '#94A3B8' },
                        ].filter(item => item.value > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => {
                        const total = iso42001Stats.total || (iso42001Stats.compliant + iso42001Stats.non_compliant + iso42001Stats.partial + (iso42001Stats.not_assessed || 0));
                        const pct = total > 0 ? (value / total * 100).toFixed(1) : 0;
                        return [`${value} (${pct}%)`, ''];
                      }} />
                    </PieChart>
                  </ResponsiveContainer>
                  
                  {/* Legend */}
                  <div className="grid grid-cols-2 gap-3 mt-4 w-full">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-green-500"></div>
                      <span className="text-sm text-slate-600">Compliant: {iso42001Stats.compliant}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-red-500"></div>
                      <span className="text-sm text-slate-600">Non-Compliant: {iso42001Stats.non_compliant}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-orange-500"></div>
                      <span className="text-sm text-slate-600">Partially Compliant: {iso42001Stats.partial}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-slate-500"></div>
                      <span className="text-sm text-slate-600">Not Applicable: {iso42001Stats.not_applicable || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-slate-400"></div>
                      <span className="text-sm text-slate-600">Not Assessed: {iso42001Stats.not_assessed || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* SOCI Compliance Status Distribution */}
              {isFrameworkEnabled('SOCI') && sociStats && (
              <div className="stat-card bg-white border-t-4 border-amber-600 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/soci')}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="px-4 py-2 rounded-sm text-sm font-semibold bg-amber-100 text-amber-800">
                    SOCI
                  </div>
                  <div className="text-sm text-slate-600">
                    {sociStats.compliance_percentage}% Complete
                  </div>
                </div>
                <h2 className="text-xl font-heading font-semibold mb-4 text-slate-900">Compliance Status Distribution</h2>
                <div className="flex flex-col items-center">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Compliant', value: sociStats.compliant, color: '#10B981' },
                          { name: 'Non-Compliant', value: sociStats.non_compliant, color: '#EF4444' },
                          { name: 'Partially Compliant', value: sociStats.partial, color: '#F59E0B' },
                          { name: 'Not Assessed', value: sociStats.not_assessed || 0, color: '#94A3B8' },
                        ].filter(item => item.value > 0)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ value }) => {
                          const total = sociStats.total || (sociStats.compliant + sociStats.non_compliant + sociStats.partial + (sociStats.not_assessed || 0));
                          const pct = total > 0 ? (value / total * 100).toFixed(0) : 0;
                          return pct > 5 ? `${pct}%` : '';
                        }}
                        outerRadius={90}
                        fill="#F59E0B"
                        dataKey="value"
                      >
                        {[
                          { name: 'Compliant', value: sociStats.compliant, color: '#10B981' },
                          { name: 'Non-Compliant', value: sociStats.non_compliant, color: '#EF4444' },
                          { name: 'Partially Compliant', value: sociStats.partial, color: '#F59E0B' },
                          { name: 'Not Assessed', value: sociStats.not_assessed || 0, color: '#94A3B8' },
                        ].filter(item => item.value > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => {
                        const total = sociStats.total || (sociStats.compliant + sociStats.non_compliant + sociStats.partial + (sociStats.not_assessed || 0));
                        const pct = total > 0 ? (value / total * 100).toFixed(1) : 0;
                        return [`${value} (${pct}%)`, ''];
                      }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-2 gap-3 mt-4 w-full">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-green-500"></div>
                      <span className="text-sm text-slate-600">Compliant: {sociStats.compliant}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-red-500"></div>
                      <span className="text-sm text-slate-600">Non-Compliant: {sociStats.non_compliant}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-orange-500"></div>
                      <span className="text-sm text-slate-600">Partially Compliant: {sociStats.partial}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-slate-500"></div>
                      <span className="text-sm text-slate-600">Not Applicable: {sociStats.not_applicable || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-sm bg-slate-400"></div>
                      <span className="text-sm text-slate-600">Not Assessed: {sociStats.not_assessed || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
          )}

          {actionStats && <ActionsDashboardSection stats={actionStats} />}

          {riskStats && <RiskDashboardSection stats={riskStats} />}

          {incidentStats && <IncidentDashboardSection stats={incidentStats} />}

          {policyStats && <PolicyDashboardSection stats={policyStats} />}

          {supplierStats && <SupplierDashboardSection stats={supplierStats} />}
        </div>
      ) : (
        <>
          {/* Single Framework Dashboard */}
          {stats && renderFrameworkStats(
            stats, 
            selectedFramework === 'ISO27001' ? 'ISO 27001' : 
            selectedFramework === 'SOC2' ? 'SOC 2' :
            selectedFramework === 'Essential8' ? 'Essential 8' :
            selectedFramework === 'PCIDSS' ? 'PCI DSS' :
            selectedFramework === 'HIPAA' ? 'HIPAA' : 'Unknown',
            'bg-slate-100 text-slate-800'
          )}
        </>
      )}
    </div>
  );
}

function ShieldCheck({ className }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  );
}